import { createApplication, enableDebugTools } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { App } from './app/app';
import { ServiceUnavailableComponent } from './app/service-unavailable/service-unavailable.component';
import { ContestSettingsService } from './app/shared/services/contest-settings.service';
import { ContestSettingsStore } from './app/shared/stores/contest-settings.store';
import { withTimeout } from './app/shared/utils/timeout';
import { ApplicationRef, ComponentRef, enableProfiling } from '@angular/core';
import { environment } from './environments/environment';
import { initMetaPixel } from './app/shared/scripts/meta-pixel';

const originalConsoleError = window.console.error;

window.console.error = function (...args): void {
  const joined: string = args.map(arg => arg?.toString?.() ?? '').join(' ');

  if (joined.includes('LockManager lock')) return;

  originalConsoleError.apply(window.console, args);
};

const TIMEOUT_MS = 5000;

async function loadContestSetting(appRef: ApplicationRef): Promise<boolean> {
  const contestSettingService = appRef.injector.get(ContestSettingsService);
  const contestSettingsStore = appRef.injector.get(ContestSettingsStore);

  try {
    await withTimeout(
      contestSettingService.initializeContestSettings(),
      TIMEOUT_MS,
      'Timeout while fetching contest settings.'
    );

    return !contestSettingsStore.contestSettings;
  } catch (error) {
    console.error(error);
    return true;
  }
}

async function initializeApp(): Promise<ComponentRef<ServiceUnavailableComponent> | ComponentRef<App>> {
  if (!environment.production) {
    enableProfiling();
  }

  if (environment.production) {
    initMetaPixel();
  }

  const appRef: ApplicationRef = await createApplication(appConfig);
  const shouldBootstrapFallback: boolean = await loadContestSetting(appRef);
  let componentRef: ComponentRef<App> | ComponentRef<ServiceUnavailableComponent>;

  if (shouldBootstrapFallback) {
    const hostElement = document.createElement('app-service-unavailable');
    document.body.appendChild(hostElement);
    componentRef = appRef.bootstrap(ServiceUnavailableComponent, hostElement);
  } else {
    componentRef = await appRef.bootstrap(App);
  }

  if (!environment.production) {
    enableDebugTools(componentRef as ComponentRef<any>);
  }

  return componentRef;
}

initializeApp().catch((err) => console.error(err));
